import React, { useEffect, useRef } from 'react';
import Konva from 'konva';
import { Stage, Layer, Group, Circle, Line, Path } from 'react-konva';
import { isNotEmpty } from '../../lib/ep';

const AnimatedTick = (props) => {
	let refs = useRef([]);

	const direction = props?.direction ?? 1;
	const display_shadow = props?.display_shadow ?? true;
	const icon_size_divider = 5;
	const stroke_width = 2;

	const default_icon_size = 100;
	const icon_size = props?.size ?? default_icon_size;
	const parent_width = (props.parent != undefined) ? document.getElementById(props.parent).getBoundingClientRect().width : icon_size * icon_size_divider;

	const scale_ini = 0;
	const scale_end = Math.max(0.3, Math.min(1, (parent_width / icon_size_divider) / 100));

	const canvas_padding = 5;
	const canvas_w = scale_end * icon_size + 2 * canvas_padding;
	const canvas_h = canvas_w;

	const highlight_color = '#FFFFFF';
	const fill_color = '#4BC319';
	const shadow_color = '#000000';
	const tick_color = '#000000';

	const hightlight_props = {
		stroke:highlight_color,
		strokeWidth:stroke_width,
	};

	const stage_offsets = {
		width:canvas_w,
		height:canvas_h,
		offsetX:-canvas_w / 2,
		offsetY:-canvas_h / 2,
	};

	const layer_props_ini = {
		offsetX:0,
		offsetY:0,
		rotation:-50 * direction,
		scaleX:scale_ini,
		scaleY:scale_ini,
	};

	const layer_props_end = {
		offsetX:0,
		offsetY:0,
		rotation:0,
		scaleX:scale_end,
		scaleY:scale_end,
	};

	const circles_props = {
		offsetX:0,
		offsetY:0,
		scaleX:1,
		scaleY:1,
	};

	const tick_props_ini = {
		offsetX:0,
		offsetY:0,
		opacity:0.2,
		rotation:70 * direction,
		scaleX:0.2,
		scaleY:0.2,
	};

	const tick_props_end = {
		offsetX:0,
		offsetY:0,
		opacity:1,
		rotation:0,
		scaleX:icon_size / default_icon_size,
		scaleY:icon_size / default_icon_size,
	};

	let anim_whole = null;
	let anim_tick = null;
	let anim_tick_light = null;
	let anim_circle_light = null;

	const tickThis = () => {
		let whole_ref = refs.current['whole_tick'];
		let tick_ref = refs.current['tick'];
		let tick_light_ref = refs.current['tick_light'];
		let circle_light_ref = refs.current['circle_light'];

		let tick_light_length = tick_light_ref.getLength();
		tick_light_ref.dash([tick_light_length]);
		tick_light_ref.dashOffset(-tick_light_length);

		let circle_light_length = circle_light_ref.getLength();
		circle_light_ref.dash([circle_light_length]);
		circle_light_ref.dashOffset(-circle_light_length);

		let whole_animation_props = {
			duration:0.15,
			...layer_props_end,
		};

		let tick_animation_props = {
			duration:0.4,
			easing:Konva.Easings.BounceEaseOut,
			...tick_props_end,
		};

		let tick_light_animation_props = {
			duration:1.4,
			dashOffset:tick_light_length,
			easing:Konva.Easings.StrongEaseInOut,
			...hightlight_props,
		};

		let circle_light_animation_props = {
			duration:1.4,
			dashOffset:circle_light_length,
			easing:Konva.Easings.StrongEaseInOut,
			...hightlight_props,
		};

		anim_tick = new Konva.Tween({
			node:tick_ref,
			...tick_animation_props,
		}).play();

		anim_tick_light = new Konva.Tween({
			node:tick_light_ref,
			...tick_light_animation_props,
		}).play();

		anim_circle_light = new Konva.Tween({
			node:circle_light_ref,
			...circle_light_animation_props,
		}).play();

		anim_whole = new Konva.Tween({
			node:whole_ref,
			...whole_animation_props,
		}).play();
	};

	useEffect(() => {
		tickThis();

		return () => {
			if(anim_whole !== null) anim_whole.finish();
			if(anim_tick !== null) anim_tick.finish();
			if(anim_tick_light !== null) anim_tick_light.finish();
			if(anim_circle_light !== null) anim_circle_light.finish();
		};
	}, []);

	let container_classes = ['gui_animated_tick'];
	if(isNotEmpty(props, '_class')) {
		container_classes.push(props._class);
	} else {
		container_classes.push((direction < 0) ? 'first_team' : 'second_team');
	}

	return (
		<div class={container_classes.join(" ")}>
			<Stage className="gui_brackets_container canvas_cont" {...stage_offsets}>
				<Layer ref={(node) => { refs.current['whole_tick'] = node; }} {...layer_props_ini}>
					<Group ref={(node) => { refs.current['circles'] = node; }} {...circles_props}>
						{(display_shadow) && (
							<Circle
								fill={shadow_color}
								radius={icon_size / 2}
								opacity={0.8}
								x={stroke_width * direction}
								y={stroke_width}
							/>
						)}

						<Circle
							fill={fill_color}
							radius={icon_size / 2}
							{...hightlight_props}
						/>

						<Path
							ref={(node) => { refs.current['circle_light'] = node; }}
							data="M37.81-28.73C29.12-40.13,15.4-47.5,0-47.5c-23.37,0-42.85,16.96-46.78,39.22"
							{...hightlight_props}
						/>
					</Group>

					<Group ref={(node) => { refs.current['tick'] = node; }} {...tick_props_ini}>
						<Line
							points={[
								-26.5, -0.5,
								-8.5, 17.5,
								26.5, -17.5,
							]}
							strokeWidth={15}
							stroke={tick_color}
						/>

						<Path
							ref={(node) => { refs.current['tick_light'] = node; }}
							data="M24.73-15.73l-3.53-3.54l-29.7,29.7L-21.2-2.26l-1.71,1.71"
							{...hightlight_props}
						/>
					</Group>
				</Layer>
			</Stage>
		</div>
	);
}

export default AnimatedTick;